import { useParams, Link, Navigate } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, User } from "lucide-react";
import Navigation from "@/components/layout/Navigation";
import Footer from "@/components/layout/Footer";
import NewsletterSubscription from "@/components/layout/NewsletterSubscription";
import ScrollReveal from "@/components/ScrollReveal";
import { getBlogPostBySlug } from "@/data/blogPosts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPostBySlug(slug) : null;

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      
      <main className="pt-24">
        {/* Article Header */}
        <section className="py-16 bg-gradient-to-b from-muted/50 to-background border-b border-border">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <Link 
                to="/blog" 
                className="inline-flex items-center text-muted-foreground hover:text-primary transition-colors mb-8 group"
              >
                <ArrowLeft className="w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1" />
                Back to Blog
              </Link>
              
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                    {post.category}
                  </Badge>
                  {post.featured && (
                    <Badge variant="outline" className="bg-muted text-muted-foreground border-border">
                      Featured
                    </Badge>
                  )}
                </div>
                
                <h1 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl leading-tight gradient-text">
                  {post.title}
                </h1>
                
                <p className="text-xl text-muted-foreground leading-relaxed">
                  {post.excerpt}
                </p>
                
                <div className="flex flex-wrap items-center gap-6 text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    <span>{post.author}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{post.date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <ScrollReveal>
          <section className="py-16">
            <div className="container mx-auto px-4 lg:px-8">
              <div className="max-w-3xl mx-auto">
                <div className="blog-content space-y-8 animate-fade-in
                    [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:font-display [&_h2]:text-foreground [&_h2]:mb-6 [&_h2]:mt-12 [&_h2]:first:mt-0
                    [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:font-display [&_h3]:text-foreground [&_h3]:mb-4 [&_h3]:mt-8
                    [&_h4]:text-xl [&_h4]:font-semibold [&_h4]:font-display [&_h4]:text-foreground [&_h4]:mb-3 [&_h4]:mt-6
                    [&_p]:text-lg [&_p]:leading-relaxed [&_p]:text-muted-foreground [&_p]:mb-6
                    [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:bg-gradient-to-r [&_blockquote]:from-primary/5 [&_blockquote]:to-primary/10 
                    [&_blockquote]:p-8 [&_blockquote]:rounded-r-xl [&_blockquote]:my-12 [&_blockquote]:text-xl 
                    [&_blockquote]:italic [&_blockquote]:text-foreground [&_blockquote]:leading-relaxed
                    [&_ul]:space-y-3 [&_ul]:mb-8 [&_ul]:pl-6
                    [&_li]:text-lg [&_li]:text-muted-foreground [&_li]:leading-relaxed [&_li]:relative
                    [&_li]:before:content-['•'] [&_li]:before:text-primary [&_li]:before:font-bold [&_li]:before:absolute [&_li]:before:-left-6
                    [&_strong]:text-foreground [&_strong]:font-semibold
                    [&_em]:text-primary [&_em]:not-italic [&_em]:font-medium
                    [&_cite]:text-muted-foreground [&_cite]:font-medium [&_cite]:not-italic
                    [&_a]:text-primary [&_a]:no-underline [&_a]:font-medium hover:[&_a]:underline
                    [&_img]:rounded-xl [&_img]:shadow-lg [&_img]:my-8 [&_img]:w-full">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {post.content}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          </section>
        </ScrollReveal>

        {/* Related Articles CTA */}
        <section className="py-16 bg-muted/50 border-t border-border">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="max-w-3xl mx-auto text-center space-y-8">
              <h2 className="text-3xl font-bold font-display text-foreground">
                Explore More Insights
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                Discover more frameworks, strategies, and data-driven insights to build 
                your content capabilities.
              </p>
              <Button asChild variant="secondary" size="lg" className="btn-interactive">
                <Link to="/blog">
                  View All Articles
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <NewsletterSubscription />
      <Footer />
    </div>
  );
};

export default BlogPost;